import type { CirclePackNode } from '../shared/types.js';
import { fetchData } from './api.js';
import { addCircleLabels } from './labels.js';
import { displayNodeData, updateSelectedNodeTitle } from './panel.js';
import { restoreHighlights, setupSearch } from './search.js';
import { setupContentFilter } from './contentFilter.js';
import { setChartInstance, setRefreshChartCallback, setCancelClickTimerCallback } from './state.js';
import { attachHoverInfo } from './hoverInfo.js';
import { attachLegend, attachLegendButton, showMyRolesOverview, clearMyRolesHighlight } from './legend.js';
import { initTheme } from './theme.js';
import { setupSettings } from './settings.js';
import { createClickHandler, createResizeHandler } from './chart/eventHandlers.js';
import { setupSvgObserver } from './chart/observers.js';
import { createRefreshChart } from './chart/refresh.js';
import { populateRoleMap } from './panel/utils.js';
import { initCookieBanner } from './cookieBanner.js';
import { t, loadLocale } from './utils/i18n.js';
import { csrfFetch } from './utils/csrfFetch.js';

// Check authentication before loading app
async function checkAuth(): Promise<boolean> {
    try {
        const response = await csrfFetch('/auth/user', {
            credentials: 'include'
        });
        await response.json();
        return true;
    } catch (error) {
        console.error('Auth check failed:', error);
        return false;
    }
}

const getColor = (node: CirclePackNode): string => {
    // Use theme-aware default color for nodes without explicit color or with white/default colors
    if (!node.color || node.color === '#ffffff' || node.color === '#fff' || node.color === 'white') {
        return getComputedStyle(document.body).getPropertyValue('--circle-default').trim();
    }
    return node.color;
};

async function initChart(): Promise<void> {
    try {
        const chartElement = document.getElementById('chart');
        const chartWidth = window.innerWidth * 0.7;

        let chartData = await fetchData();
        if (!chartData) {
            chartElement.innerHTML = '<p class="role-panel__message role-panel__message--error">Cannot connect to server. Please run: npm run server</p>';
            return;
        }
        
        // Populate global roleMap with current role names
        const dataArray = Array.isArray(chartData) ? chartData : [chartData];
        populateRoleMap(dataArray);

        // Click timer management
        const clickTimerRef = { current: null as number | null };
        const cancelClickTimer = () => {
            if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
            }
        };
        setCancelClickTimerCallback(cancelClickTimer);
        
        const chart = new ((window as any).CirclePack)(chartElement)
            .width(chartWidth)
            .height(window.innerHeight)
            .data(chartData)
            .size('value')
            .color(getColor)
            .borderWidth(0)
            .padding(10)
            .showTooltip(() => false);  // Workaround for library bug in v1.10.4
        
        // Attach click handler after chart is created
        chart.onClick(createClickHandler(chart, clickTimerRef));

        // Initialize chart features after render
        setTimeout(() => {
            addCircleLabels();
            restoreHighlights();
            attachHoverInfo(chartElement, chart);
            
            // NEW: Icon at top right for my roles
            attachMyRolesIcon(chartElement);
            
            // NEW: Text button at bottom right for legend
            const chartWrapper = chartElement.parentElement;
            if (chartWrapper) {
                attachLegendButton(chartWrapper, chartData);
            }
            
            setupSvgObserver(chartElement);
        }, 100);

        setChartInstance(chart);
        (window as any).addCircleLabels = addCircleLabels;
        (window as any).displayNodeData = displayNodeData;
        displayNodeData(null);

        // Setup chart refresh and expose it
        const refreshChart = createRefreshChart(chart);
        setRefreshChartCallback(refreshChart);

        // Handle window resize
        window.addEventListener('resize', createResizeHandler(chart));
    } catch (error) {
        console.error('Error creating chart:', error);
        const chartElement = document.getElementById('chart');
        const errorMsg = document.createElement('p');
        errorMsg.className = 'role-panel__message role-panel__message--error';
        errorMsg.textContent = `Error: ${error.message}`;
        chartElement.innerHTML = '';
        chartElement.appendChild(errorMsg);
    }
}

export function attachMyRolesButton(chartElement: HTMLElement): void {
    const button = document.createElement('button');
    button.className = 'icon-button__my-roles-toggle';
    button.textContent = t('main.showMyRoles');
    
    let isActive = false;
    
    button.addEventListener('click', async () => {
        if (button.classList.contains('loading')) return;
        
        isActive = !isActive;
        
        if (isActive) {
            button.textContent = t('common.loading');
            button.classList.add('loading');
            
            await showMyRolesOverview();
            
            button.textContent = t('main.hideMyRoles');
            button.classList.remove('loading');
            button.classList.add('active');
        } else {
            button.textContent = t('main.showMyRoles');
            button.classList.remove('active');
            clearMyRolesHighlight();
        }
    });
    
    const wrapper = chartElement.parentElement;
    if (wrapper) {
        wrapper.appendChild(button);
    }
}

// New function: Create my roles as an icon at top right
export function attachMyRolesIcon(chartElement: HTMLElement): void {
    const icon = document.createElement('button');
    icon.className = 'icon-button__my-roles';
    icon.title = 'Show my roles';
    icon.setAttribute('aria-label', 'Toggle my roles view');
    
    // User icon SVG
    icon.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
        </svg>
    `;
    
    let isActive = false;
    
    icon.addEventListener('click', async () => {
        if (icon.classList.contains('loading')) return;
        
        isActive = !isActive;
        
        if (isActive) {
            icon.classList.add('loading');
            
            await showMyRolesOverview();
            
            icon.classList.remove('loading');
            icon.classList.add('active');
        } else {
            icon.classList.remove('active');
            clearMyRolesHighlight();
        }
    });
    
    chartElement.appendChild(icon);
}

async function bootstrap(): Promise<void> {
    // Check if user is authenticated
    const isAuthenticated = await checkAuth();
    if (!isAuthenticated) {
        // Redirect to login page (safely handle test environment)
        try {
            window.location.href = '/login.html';
        } catch (error) {
            // In test environment, location change may fail - that's ok
            console.warn('Could not redirect to login (test environment?)');
        }
        return;
    }

    // Set initial page title from config
    try {
        const response = await fetch('/api/config');
        if (response.ok) {
            const config = await response.json();
            document.title = `${config.appName} – ${config.appTagline}`;
        }
    } catch (error) {
        console.error('Failed to fetch app config:', error);
    }

    initTheme();
    initCookieBanner();
    
    // Initialize i18n before setting translated text
    loadLocale();
    
    // Initialize static translated text
    const panelHeaderText = document.getElementById('panel-header-text');
    if (panelHeaderText) {
        panelHeaderText.textContent = t('panel.header');
    }
    
    initChart();
    setupSearch();
    setupContentFilter();
    setupSettings();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
} else {
    bootstrap();
}
